import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Users,
  DollarSign,
  TrendingUp,
  Shield,
  Settings,
  Search,
  Filter,
  MoreVertical,
  Ban,
  CheckCircle,
  AlertTriangle,
  Eye,
  Trash2,
  Download,
  RefreshCw,
  Crown,
  Zap,
  Star,
  Gem,
  Lock,
  Key,
  ShieldCheck,
  Plus,
  Upload,
  FileText,
  CreditCard,
  Receipt,
  Calendar,
  Hash,
  Clock,
  Wallet,
  ArrowUpRight
} from "lucide-react";
import { toast } from "@/components/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import AdminWallet from "@/components/admin/AdminWallet";
import AdminMarketers from "@/components/admin/AdminMarketers";
import AdminWithdrawals from "@/components/admin/AdminWithdrawals";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Plan configurations
const PLANS = {
  free: {
    id: 'free',
    name: 'Free',
    nameAr: 'مجانية',
    icon: Star,
    color: '#71717A',
    monthlyPrice: 0,
    yearlyPrice: 0,
    membersLimit: 100,
    features: ['100 مشترك', 'شهر تجريبي', 'دعم بالبريد']
  },
  standard: {
    id: 'standard',
    name: 'Standard',
    nameAr: 'قياسية',
    icon: Zap,
    color: '#00F0FF',
    monthlyPrice: 24.99,
    yearlyPrice: 200.92,
    membersLimit: 100000,
    features: ['100,000 مشترك', 'أسبوع مجاني', 'دعم أولوية', 'تصدير CSV']
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    nameAr: 'احترافية',
    icon: Crown,
    color: '#7000FF',
    monthlyPrice: 49.99,
    yearlyPrice: 401.92,
    membersLimit: 250000,
    features: ['250,000 مشترك', 'شهر مجاني (سنوي)', 'API كامل', 'دعم 24/7']
  },
  platinum: {
    id: 'platinum',
    name: 'Platinum',
    nameAr: 'بلاتينية',
    icon: Gem,
    color: '#FF9500',
    monthlyPrice: 99.99,
    yearlyPrice: 795.96,
    membersLimit: 500000,
    features: ['500,000 مشترك', 'مدير حساب', 'تكاملات مخصصة']
  },
  expert: {
    id: 'expert',
    name: 'Expert',
    nameAr: 'الخبراء',
    icon: Shield,
    color: '#FF003C',
    monthlyPrice: 149.99,
    yearlyPrice: 1206.92,
    membersLimit: 1000000,
    features: ['1,000,000 مشترك', 'خادم مخصص', 'دعم VIP']
  }
};

const StatCard = ({ icon: Icon, label, value, color = "#00F0FF", trend, subtext }) => (
  <div className="glass rounded-xl p-5 card-hover">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-neutral-500">{label}</p>
        <p className="text-2xl font-mono font-bold mt-1" style={{ color }}>{value}</p>
        {subtext && <p className="text-xs text-neutral-600 mt-1">{subtext}</p>}
      </div>
      <div 
        className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ background: `${color}20` }}
      >
        <Icon size={20} style={{ color }} />
      </div>
    </div>
    {trend && (
      <div className="flex items-center gap-1 mt-3">
        <TrendingUp size={14} className="text-green-500" />
        <span className="text-xs text-green-500">{trend}</span>
      </div>
    )}
  </div>
);

const SubscriberRow = ({ subscriber, onAction, isArabic, t, getPlanName }) => {
  const plan = PLANS[subscriber.plan] || PLANS.free;
  const PlanIcon = plan.icon;
  const isActive = subscriber.status === 'active';
  const isSuspended = subscriber.status === 'suspended';

  const getStatusText = () => {
    if (isArabic) {
      return isActive ? 'نشط' : isSuspended ? 'موقوف' : 'منتهي';
    }
    return isActive ? 'Active' : isSuspended ? 'Suspended' : 'Expired';
  };

  return (
    <tr className="table-row" data-testid={`subscriber-${subscriber.id}`}>
      <td className="table-cell">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
            {subscriber.name?.charAt(0) || 'U'}
          </div>
          <div>
            <p className="font-semibold">{subscriber.name || (isArabic ? 'مستخدم' : 'User')}</p>
            <p className="text-xs text-neutral-500">{subscriber.email}</p>
          </div>
        </div>
      </td>
      <td className="table-cell">
        <div className="flex items-center gap-2">
          <PlanIcon size={16} style={{ color: plan.color }} />
          <span style={{ color: plan.color }}>{getPlanName ? getPlanName(plan) : plan.nameAr}</span>
        </div>
      </td>
      <td className="table-cell">
        <span className={`badge ${
          isActive ? 'badge-success' : 
          isSuspended ? 'badge-error' : 'badge-warning'
        }`}>
          {getStatusText()}
        </span>
      </td>
      <td className="table-cell font-mono">
        {subscriber.members_collected?.toLocaleString() || 0}
      </td>
      <td className="table-cell text-neutral-500">
        {subscriber.subscription_end ? 
          new Date(subscriber.subscription_end).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US') : 
          '-'}
      </td>
      <td className="table-cell">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onAction('view', subscriber)}>
              <Eye size={14} className={isArabic ? "ml-2" : "mr-2"} />
              {t ? t('admin.viewDetails') : 'View Details'}
            </DropdownMenuItem>
            {isActive ? (
              <DropdownMenuItem 
                onClick={() => onAction('suspend', subscriber)}
                className="text-yellow-400"
              >
                <Ban size={14} className={isArabic ? "ml-2" : "mr-2"} />
                {t ? t('admin.suspendAccount') : 'Suspend Account'}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem 
                onClick={() => onAction('activate', subscriber)}
                className="text-green-400"
              >
                <CheckCircle size={14} className={isArabic ? "ml-2" : "mr-2"} />
                {t ? t('admin.activateAccount') : 'Activate Account'}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem 
              onClick={() => onAction('delete', subscriber)}
              className="text-red-400"
            >
              <Trash2 size={14} className={isArabic ? "ml-2" : "mr-2"} />
              {t ? t('admin.deleteAccount') : 'Delete Account'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
};

const SubscriberDetail = ({ subscriber, onClose, onAction, t, isArabic, getPlanName }) => {
  if (!subscriber) return null;
  
  const plan = PLANS[subscriber.plan] || PLANS.free;
  const PlanIcon = plan.icon;

  return (
    <Dialog open={!!subscriber} onOpenChange={onClose}>
      <DialogContent className="glass border-white/10 max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t ? t('admin.subscriberDetails') : 'Subscriber Details'}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="flex items-center gap-4 p-4 bg-black/30 rounded-lg">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center text-2xl font-bold text-black">
              {subscriber.name?.charAt(0) || 'U'}
            </div>
            <div>
              <h3 className="text-xl font-bold">{subscriber.name || (isArabic ? 'مستخدم' : 'User')}</h3>
              <p className="text-neutral-400">{subscriber.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <PlanIcon size={16} style={{ color: plan.color }} />
                <span style={{ color: plan.color }}>{getPlanName ? getPlanName(plan) : plan.name}</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-black/30 rounded-lg p-4 text-center">
              <p className="text-2xl font-mono font-bold text-cyan-400">
                {subscriber.members_collected?.toLocaleString() || 0}
              </p>
              <p className="text-xs text-neutral-500 mt-1">{t ? t('admin.membersCollected') : 'Members Collected'}</p>
            </div>
            <div className="bg-black/30 rounded-lg p-4 text-center">
              <p className="text-2xl font-mono font-bold text-green-400">
                {subscriber.groups_monitored || 0}
              </p>
              <p className="text-xs text-neutral-500 mt-1">{t ? t('admin.groups') : 'Groups'}</p>
            </div>
            <div className="bg-black/30 rounded-lg p-4 text-center">
              <p className="text-2xl font-mono font-bold text-violet-400">
                {plan.membersLimit.toLocaleString()}
              </p>
              <p className="text-xs text-neutral-500 mt-1">{t ? t('admin.maxLimit') : 'Max Limit'}</p>
            </div>
          </div>

          {/* Usage Progress */}
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-neutral-400">{t ? t('admin.quotaUsage') : 'Quota Usage'}</span>
              <span className="font-mono">
                {Math.round((subscriber.members_collected || 0) / plan.membersLimit * 100)}%
              </span>
            </div>
            <div className="h-2 bg-neutral-800 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all"
                style={{ 
                  width: `${Math.min((subscriber.members_collected || 0) / plan.membersLimit * 100, 100)}%`,
                  background: plan.color
                }}
              />
            </div>
          </div>

          {/* Subscription Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-black/30 rounded-lg p-4">
              <p className="text-xs text-neutral-500">{t ? t('admin.subscriptionDate') : 'Subscription Date'}</p>
              <p className="font-mono mt-1">
                {subscriber.subscription_start ? 
                  new Date(subscriber.subscription_start).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US') : '-'}
              </p>
            </div>
            <div className="bg-black/30 rounded-lg p-4">
              <p className="text-xs text-neutral-500">{t ? t('admin.expiryDate') : 'Expiry Date'}</p>
              <p className="font-mono mt-1">
                {subscriber.subscription_end ? 
                  new Date(subscriber.subscription_end).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US') : '-'}
              </p>
            </div>
          </div>

          {/* Payment History */}
          <div>
            <h4 className="font-semibold mb-3">{t ? t('admin.paymentsRecord') : 'Payment History'}</h4>
            <div className="bg-black/30 rounded-lg p-4">
              {subscriber.payments?.length > 0 ? (
                <div className="space-y-2">
                  {subscriber.payments.slice(0, 5).map((payment, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
                      <div>
                        <p className="text-sm">${payment.amount}</p>
                        <p className="text-xs text-neutral-500">{payment.tx_hash?.slice(0, 20)}...</p>
                      </div>
                      <span className="text-xs text-green-400">{t ? t('admin.confirmed') : 'Confirmed'}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-neutral-500">{t ? t('admin.noPayments') : 'No payments'}</p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t ? t('admin.close') : 'Close'}</Button>
          {subscriber.status === 'active' ? (
            <Button 
              variant="destructive" 
              onClick={() => { onAction('suspend', subscriber); onClose(); }}
            >
              <Ban size={16} className={isArabic ? "ml-2" : "mr-2"} />
              {t ? t('admin.suspendAccount') : 'Suspend Account'}
            </Button>
          ) : (
            <Button 
              onClick={() => { onAction('activate', subscriber); onClose(); }}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle size={16} className={isArabic ? "ml-2" : "mr-2"} />
              {t ? t('admin.activateAccount') : 'Activate Account'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Change Password Modal Component
const ChangePasswordModal = ({ isOpen, onClose, t, isArabic }) => {
  const [step, setStep] = useState(1); // 1 = request OTP, 2 = enter OTP & new password
  const [sessionId, setSessionId] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpHint, setOtpHint] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const { getToken } = useAuth();

  const handleRequestOTP = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/request-password-change`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      setSessionId(response.data.session_id);
      setOtpHint(response.data.otp_hint);
      setStep(2);
      toast.success(t ? t('admin.otpSent') : 'OTP sent successfully');
      toast.info(`${t ? t('admin.otpHint') : 'OTP Code'}: ${response.data.otp_hint}`, { duration: 10000 });
    } catch (error) {
      toast.error(error.response?.data?.detail || (t ? t('admin.errorSendingOtp') : 'Failed to send OTP'));
    }
    setLoading(false);
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error(t ? t('admin.passwordMismatch') : 'New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error(t ? t('admin.passwordMinLength') : 'Password must be at least 6 characters');
      return;
    }

    if (otpCode.length !== 6) {
      toast.error(t ? t('admin.otpMustBe6Digits') : 'OTP must be 6 digits');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
        otp: otpCode,
        session_id: sessionId
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      toast.success(t ? t('admin.passwordChanged') : 'Password changed successfully!');
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || (t ? t('admin.errorChangingPassword') : 'Failed to change password'));
    }
    setLoading(false);
  };

  const handleClose = () => {
    setStep(1);
    setSessionId('');
    setOtpCode('');
    setOtpHint('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="glass border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="text-red-400" />
            {t ? t('admin.changePassword') : 'Change Password'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Step 1: Request OTP */}
          {step === 1 && (
            <>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 text-center">
                <ShieldCheck className="mx-auto text-amber-400 mb-2" size={40} />
                <p className="text-sm text-neutral-300">
                  {t ? t('admin.securityVerificationDesc') : 'To change password, a verification code (2FA) will be sent to verify your identity'}
                </p>
              </div>

              <Button
                onClick={handleRequestOTP}
                disabled={loading}
                className="w-full bg-red-600 hover:bg-red-700"
              >
                {loading ? (t ? t('admin.sendingOtp') : 'Sending...') : (t ? t('admin.requestOtp') : 'Request OTP')}
              </Button>
            </>
          )}

          {/* Step 2: Enter OTP and new password */}
          {step === 2 && (
            <>
              {/* OTP Hint */}
              {otpHint && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-center">
                  <p className="text-xs text-amber-400">
                    🔑 {t ? t('admin.otpHint') : 'OTP Code (for testing)'}: <span className="font-mono font-bold">{otpHint}</span>
                  </p>
                </div>
              )}

              {/* OTP Input */}
              <div>
                <label className="text-sm text-neutral-400 block mb-2">{t ? t('admin.otpInput') : 'OTP Code (2FA)'}</label>
                <Input
                  data-testid="change-password-otp"
                  type="text"
                  placeholder="000000"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-xl tracking-[0.3em] font-mono"
                  dir="ltr"
                  maxLength={6}
                />
              </div>

              {/* Current Password */}
              <div>
                <label className="text-sm text-neutral-400 block mb-2">{t ? t('admin.currentPassword') : 'Current Password'}</label>
                <div className="relative">
                  <Lock className={`absolute ${isArabic ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-neutral-500`} size={18} />
                  <Input
                    data-testid="current-password"
                    type={showCurrentPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className={isArabic ? "pr-10" : "pl-10"}
                    dir="ltr"
                  />
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="text-sm text-neutral-400 block mb-2">{t ? t('admin.newPassword') : 'New Password'}</label>
                <div className="relative">
                  <Key className={`absolute ${isArabic ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-neutral-500`} size={18} />
                  <Input
                    data-testid="new-password"
                    type={showNewPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={isArabic ? "pr-10" : "pl-10"}
                    dir="ltr"
                  />
                </div>
              </div>

              {/* Confirm New Password */}
              <div>
                <label className="text-sm text-neutral-400 block mb-2">{t ? t('admin.confirmNewPassword') : 'Confirm New Password'}</label>
                <Input
                  data-testid="confirm-password"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  dir="ltr"
                />
              </div>

              <Button
                onClick={handleChangePassword}
                disabled={loading || otpCode.length !== 6 || !currentPassword || !newPassword || newPassword !== confirmPassword}
                className="w-full bg-red-600 hover:bg-red-700"
              >
                {loading ? (t ? t('admin.changingPassword') : 'Changing...') : (t ? t('admin.changePasswordBtn') : 'Change Password')}
              </Button>

              <button
                onClick={() => setStep(1)}
                className="w-full text-center text-sm text-neutral-500 hover:text-neutral-400"
              >
                {isArabic ? '← العودة' : '← Back'}
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Payments Table Component
const PaymentsTable = ({ payments, loading, t, isArabic }) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredPayments = payments.filter(p => {
    if (!searchTerm) return true;
    return (
      p.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.tx_hash?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className={`absolute ${isArabic ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-neutral-500`} size={18} />
        <Input
          placeholder={t ? t('admin.searchByNameEmailTx') : 'Search by name, email or transaction...'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={isArabic ? "pr-10" : "pl-10"}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="data-table w-full">
          <thead>
            <tr>
              <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t ? t('admin.subscriber') : 'Subscriber'}</th>
              <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t ? t('admin.plan') : 'Plan'}</th>
              <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t ? t('admin.amount') : 'Amount'}</th>
              <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t ? t('admin.transactionNumber') : 'Transaction #'}</th>
              <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t ? t('admin.invoiceNumber') : 'Invoice #'}</th>
              <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t ? t('admin.date') : 'Date'}</th>
              <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t ? t('admin.status') : 'Status'}</th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-8 text-neutral-500">
                  <Receipt className="mx-auto mb-2" size={40} />
                  <p>{t ? t('admin.noPayments') : 'No payments'}</p>
                </td>
              </tr>
            ) : (
              filteredPayments.map((payment, idx) => {
                const plan = PLANS[payment.plan] || PLANS.free;
                const PlanIcon = plan.icon;
                
                return (
                  <tr key={payment.id || idx} className="table-row">
                    <td className="table-cell">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm">
                          {payment.user_name?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{payment.user_name || (isArabic ? 'غير معروف' : 'Unknown')}</p>
                          <p className="text-xs text-neutral-500">{payment.user_email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-2">
                        <PlanIcon size={14} style={{ color: plan.color }} />
                        <span className="text-sm" style={{ color: plan.color }}>{isArabic ? plan.nameAr : plan.name}</span>
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className="font-mono font-bold text-green-400">${payment.amount?.toFixed(2)}</span>
                      <p className="text-xs text-neutral-500">
                        {payment.billing_cycle === 'yearly' ? (isArabic ? 'سنوي' : 'Yearly') : (isArabic ? 'شهري' : 'Monthly')}
                      </p>
                    </td>
                    <td className="table-cell">
                      <code className="text-xs bg-black/30 px-2 py-1 rounded font-mono">
                        {payment.tx_hash?.slice(0, 16)}...
                      </code>
                    </td>
                    <td className="table-cell">
                      <span className="text-sm text-cyan-400 font-mono">
                        {payment.invoice_number || '-'}
                      </span>
                    </td>
                    <td className="table-cell text-sm text-neutral-400">
                      {payment.created_at ? 
                        new Date(payment.created_at).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                    </td>
                    <td className="table-cell">
                      <span className={`badge ${
                        payment.status === 'completed' ? 'badge-success' : 
                        payment.status === 'pending' ? 'badge-warning' : 'badge-error'
                      }`}>
                        {payment.status === 'completed' ? (t ? t('admin.completed') : 'Completed') : 
                         payment.status === 'pending' ? (t ? t('admin.pending') : 'Pending') : (t ? t('admin.failed') : 'Failed')}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      
      {/* Summary */}
      {filteredPayments.length > 0 && (
        <div className="flex justify-between items-center text-sm text-neutral-500 pt-4 border-t border-white/10">
          <span>{t ? t('admin.totalPayments') : 'Total Payments'}: {filteredPayments.length}</span>
          <span>
            {t ? t('admin.totalRevenue') : 'Total Revenue'}: 
            <span className={`font-mono text-green-400 ${isArabic ? 'mr-1' : 'ml-1'}`}>
              ${filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)}
            </span>
          </span>
        </div>
      )}
    </div>
  );
};

// Subscriptions Table Component
const SubscriptionsTable = ({ subscriptions, loading, t, isArabic, getPlanName }) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredSubscriptions = subscriptions.filter(s => {
    if (!searchTerm) return true;
    return (
      s.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className={`absolute ${isArabic ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-neutral-500`} size={18} />
        <Input
          placeholder={t ? t('admin.searchByNameEmailInvoice') : 'Search by name, email or invoice...'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={isArabic ? "pr-10" : "pl-10"}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="data-table w-full">
          <thead>
            <tr>
              <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t ? t('admin.subscriber') : 'Subscriber'}</th>
              <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t ? t('admin.plan') : 'Plan'}</th>
              <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t ? t('admin.amount') : 'Amount'}</th>
              <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t ? t('admin.startDate') : 'Start Date'}</th>
              <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t ? t('admin.endDate') : 'End Date'}</th>
              <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t ? t('admin.autoRenewal') : 'Auto Renewal'}</th>
              <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t ? t('admin.status') : 'Status'}</th>
            </tr>
          </thead>
          <tbody>
            {filteredSubscriptions.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-8 text-neutral-500">
                  <CreditCard className="mx-auto mb-2" size={40} />
                  <p>{t ? t('admin.noSubscriptions') : 'No subscriptions'}</p>
                </td>
              </tr>
            ) : (
              filteredSubscriptions.map((sub, idx) => {
                const plan = PLANS[sub.plan] || PLANS.free;
                const PlanIcon = plan.icon;
                const isExpired = sub.end_date && new Date(sub.end_date) < new Date();
                
                return (
                  <tr key={sub.id || idx} className="table-row">
                    <td className="table-cell">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm">
                          {sub.user_name?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{sub.user_name || (isArabic ? 'غير معروف' : 'Unknown')}</p>
                          <p className="text-xs text-neutral-500">{sub.user_email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-2">
                        <PlanIcon size={14} style={{ color: plan.color }} />
                        <span className="text-sm" style={{ color: plan.color }}>{getPlanName ? getPlanName(plan) : plan.name}</span>
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className="font-mono font-bold text-green-400">${sub.amount?.toFixed(2)}</span>
                      <p className="text-xs text-neutral-500">
                        {sub.billing_cycle === 'yearly' ? (isArabic ? 'سنوي' : 'Yearly') : (isArabic ? 'شهري' : 'Monthly')}
                      </p>
                    </td>
                    <td className="table-cell text-sm">
                      {sub.start_date ? 
                        new Date(sub.start_date).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US') : '-'}
                    </td>
                    <td className="table-cell text-sm">
                      <span className={isExpired ? 'text-red-400' : 'text-green-400'}>
                        {sub.end_date ? 
                          new Date(sub.end_date).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US') : '-'}
                      </span>
                    </td>
                    <td className="table-cell">
                      {sub.auto_renew ? (
                        <span className="text-green-400 flex items-center gap-1">
                          <CheckCircle size={14} /> {t ? t('admin.enabled') : 'Enabled'}
                        </span>
                      ) : (
                        <span className="text-neutral-500">{isArabic ? 'غير مفعّل' : 'Disabled'}</span>
                      )}
                    </td>
                    <td className="table-cell">
                      <span className={`badge ${
                        sub.status === 'active' && !isExpired ? 'badge-success' : 
                        isExpired ? 'badge-error' : 'badge-warning'
                      }`}>
                        {sub.status === 'active' && !isExpired ? (t ? t('admin.active') : 'Active') : 
                         isExpired ? (t ? t('admin.expired') : 'Expired') : sub.status}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Staff Access Manager Component
const StaffAccessManager = ({ token, t, isArabic }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchEmails = async () => {
    try {
      const response = await axios.get(`${API}/allowed-staff-emails`, { headers });
      setEmails(response.data.emails || []);
    } catch (error) {
      console.error('Error fetching staff emails:', error);
      toast.error(t ? t('admin.errorFetchingData') : 'Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmails();
  }, []);

  const handleAddEmail = async (e) => {
    e.preventDefault();
    if (!newEmail || !newEmail.includes('@')) {
      toast.error(isArabic ? 'يرجى إدخال بريد إلكتروني صحيح' : 'Please enter a valid email');
      return;
    }

    setAdding(true);
    try {
      await axios.post(`${API}/allowed-staff-emails?email=${encodeURIComponent(newEmail)}`, {}, { headers });
      toast.success(t ? t('admin.emailAdded') : 'Email added successfully');
      setNewEmail('');
      fetchEmails();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(t ? t('admin.emailExists') : 'Email already exists');
      } else {
        toast.error(t ? t('admin.errorAddingEmail') : 'Error adding email');
      }
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteEmail = async (email) => {
    const confirmMsg = t ? `${t('admin.confirmDeleteEmail')} ${email}?` : `Are you sure you want to delete ${email}?`;
    if (!window.confirm(confirmMsg)) return;

    setDeleting(email);
    try {
      await axios.delete(`${API}/allowed-staff-emails/${encodeURIComponent(email)}`, { headers });
      toast.success(t ? t('admin.emailDeleted') : 'Email deleted');
      fetchEmails();
    } catch (error) {
      toast.error(t ? t('admin.errorDeletingEmail') : 'Error deleting email');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="glass rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
            <ShieldCheck size={24} className="text-emerald-400" />
          </div>
          <div>
            <h3 className="text-xl font-bold">{t ? t('admin.staffAccessManagement') : 'Staff Access Management'}</h3>
            <p className="text-sm text-neutral-500">{t ? t('admin.staffAccessDescription') : 'List of emails allowed to access during maintenance'}</p>
          </div>
        </div>
        <div className="text-sm text-neutral-400">
          {emails.length} {t ? t('admin.registeredEmails') : 'registered emails'}
        </div>
      </div>

      {/* Add New Email Form */}
      <form onSubmit={handleAddEmail} className="flex gap-3 mb-6">
        <div className="relative flex-1">
          <Input
            type="email"
            placeholder={t ? t('admin.enterNewEmail') : 'Enter new email...'}
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className={isArabic ? "pr-4" : "pl-4"}
            dir="ltr"
            data-testid="new-staff-email-input"
          />
        </div>
        <Button 
          type="submit"
          disabled={adding || !newEmail}
          className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
          data-testid="add-staff-email-btn"
        >
          {adding ? (
            <>{t ? t('admin.adding') : 'Adding...'}</>
          ) : (
            <>
              <Plus size={18} className={isArabic ? "ml-2" : "mr-2"} />
              {t ? t('admin.add') : 'Add'}
            </>
          )}
        </Button>
      </form>

      {/* Emails List */}
      <div className="space-y-2">
        {emails.length === 0 ? (
          <div className="text-center py-8 text-neutral-500">
            <ShieldCheck size={48} className="mx-auto mb-3 opacity-50" />
            <p>{t ? t('admin.noRegisteredEmails') : 'No registered emails'}</p>
          </div>
        ) : (
          emails.map((item, index) => (
            <div 
              key={item.email || index}
              className="flex items-center justify-between p-4 bg-black/30 rounded-lg hover:bg-black/40 transition-colors"
              data-testid={`staff-email-${index}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold">
                  {item.email?.charAt(0).toUpperCase() || '?'}
                </div>
                <div>
                  <p className="font-mono text-sm" dir="ltr">{item.email}</p>
                  <p className="text-xs text-neutral-500">
                    {t ? t('admin.addedOn') : 'Added on'}: {item.added_at ? new Date(item.added_at).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US') : (t ? t('admin.unknown') : 'Unknown')}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeleteEmail(item.email)}
                disabled={deleting === item.email}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                data-testid={`delete-staff-email-${index}`}
              >
                {deleting === item.email ? (
                  <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Trash2 size={16} />
                )}
              </Button>
            </div>
          ))
        )}
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber-400 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-400">{t ? t('admin.importantNote') : 'Important Note'}</p>
            <p className="text-sm text-neutral-400 mt-1">
              {t ? t('admin.staffAccessNote') : 'This list determines who can access the login page during maintenance mode. The user must have a registered account on the platform with the same email.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { isArabic } = useLanguage();
  const [stats, setStats] = useState({
    total_subscribers: 0,
    active_subscribers: 0,
    total_revenue: 0,
    this_month_revenue: 0
  });
  const [subscribers, setSubscribers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [activeTab, setActiveTab] = useState('subscribers');
  const { getToken } = useAuth();

  // Get plan name based on language
  const getPlanName = (plan) => isArabic ? plan.nameAr : plan.name;
  
  // Get status text based on language
  const getStatusText = (status) => {
    if (isArabic) {
      return status === 'active' ? 'نشط' : status === 'suspended' ? 'موقوف' : 'منتهي';
    }
    return status === 'active' ? 'Active' : status === 'suspended' ? 'Suspended' : 'Expired';
  };

  // Export Report Function
  const handleExportReport = () => {
    const reportData = {
      generated_at: new Date().toISOString(),
      stats: stats,
      subscribers: subscribers.map(s => ({
        name: s.name,
        email: s.email,
        plan: getPlanName(PLANS[s.plan] || PLANS.free),
        status: getStatusText(s.status),
        members_collected: s.members_collected,
        subscription_end: s.subscription_end
      }))
    };
    
    const dataStr = JSON.stringify(reportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `admin-report-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(t('admin.reportExported'));
  };

  // Create Member File
  const handleCreateMemberFile = () => {
    const template = {
      members: [
        { name: isArabic ? "اسم العضو" : "Member Name", email: "email@example.com", phone: "+966500000000", notes: isArabic ? "ملاحظات" : "Notes" }
      ]
    };
    const dataStr = JSON.stringify(template, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "members-template.json";
    link.click();
    URL.revokeObjectURL(url);
    toast.success(t('admin.templateCreated'));
  };

  // Export Members
  const handleExportMembers = () => {
    const membersData = subscribers.map(s => ({
      id: s.id,
      name: s.name,
      email: s.email,
      plan: getPlanName(PLANS[s.plan] || PLANS.free),
      status: s.status,
      members_collected: s.members_collected,
      subscription_end: s.subscription_end
    }));
    
    const dataStr = JSON.stringify(membersData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `members-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(t('admin.membersExported'));
  };

  // Import Members
  const handleImportMembers = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.csv';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        try {
          const text = await file.text();
          const data = JSON.parse(text);
          const count = data.members?.length || data.length || 0;
          toast.success(`${count} ${t('admin.membersImported')}`);
        } catch (error) {
          toast.error(t('admin.errorReadingFile'));
        }
      }
    };
    input.click();
  };

  const fetchData = async () => {
    const token = getToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    try {
      const [statsRes, subsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/subscribers`, { headers })
      ]);
      setStats(statsRes.data);
      setSubscribers(subsRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      // Demo data
      setStats({
        total_subscribers: 156,
        active_subscribers: 142,
        total_revenue: 12580.50,
        this_month_revenue: 2340.00
      });
      setSubscribers([
        { id: '1', name: 'أحمد محمد', email: 'ahmed@example.com', plan: 'pro', status: 'active', members_collected: 45000, subscription_end: '2025-06-15' },
        { id: '2', name: 'سارة علي', email: 'sara@example.com', plan: 'standard', status: 'active', members_collected: 12500, subscription_end: '2025-02-20' },
        { id: '3', name: 'محمد خالد', email: 'mohamed@example.com', plan: 'platinum', status: 'suspended', members_collected: 89000, subscription_end: '2025-04-10' },
        { id: '4', name: 'فاطمة أحمد', email: 'fatima@example.com', plan: 'free', status: 'active', members_collected: 85, subscription_end: '2025-01-30' },
        { id: '5', name: 'عمر سعيد', email: 'omar@example.com', plan: 'expert', status: 'active', members_collected: 520000, subscription_end: '2025-12-01' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    const token = getToken();
    if (!token) return;
    
    setPaymentsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data.data || []);
    } catch (error) {
      console.error("Error fetching payments:", error);
      // Demo data for payments
      setPayments([
        { id: '1', user_name: 'أحمد محمد', user_email: 'ahmed@example.com', plan: 'pro', amount: 49.99, billing_cycle: 'monthly', tx_hash: 'abc123xyz789def456', invoice_number: 'INV-20260128-ABC123', status: 'completed', created_at: '2026-01-28T10:30:00Z' },
        { id: '2', user_name: 'سارة علي', user_email: 'sara@example.com', plan: 'standard', amount: 200.92, billing_cycle: 'yearly', tx_hash: 'def456ghi789jkl012', invoice_number: 'INV-20260127-DEF456', status: 'completed', created_at: '2026-01-27T14:15:00Z' },
        { id: '3', user_name: 'محمد خالد', user_email: 'mohamed@example.com', plan: 'platinum', amount: 99.99, billing_cycle: 'monthly', tx_hash: 'ghi789jkl012mno345', invoice_number: 'INV-20260126-GHI789', status: 'pending', created_at: '2026-01-26T09:45:00Z' },
      ]);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    const token = getToken();
    if (!token) return;
    
    setSubscriptionsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptions(response.data.data || []);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      // Demo data for subscriptions
      setSubscriptions([
        { id: '1', user_name: 'أحمد محمد', user_email: 'ahmed@example.com', plan: 'pro', amount: 49.99, billing_cycle: 'monthly', start_date: '2026-01-28T00:00:00Z', end_date: '2026-03-28T00:00:00Z', auto_renew: true, status: 'active' },
        { id: '2', user_name: 'سارة علي', user_email: 'sara@example.com', plan: 'standard', amount: 200.92, billing_cycle: 'yearly', start_date: '2026-01-27T00:00:00Z', end_date: '2027-02-03T00:00:00Z', auto_renew: false, status: 'active' },
      ]);
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchPayments();
    fetchSubscriptions();
  }, []);

  const handleAction = async (action, subscriber) => {
    switch (action) {
      case 'view':
        setSelectedSubscriber(subscriber);
        break;
      case 'suspend':
        try {
          await axios.post(`${API}/admin/subscribers/${subscriber.id}/suspend`);
          toast.success('تم إيقاف الحساب');
          fetchData();
        } catch (e) {
          // Demo mode
          setSubscribers(subs => subs.map(s => 
            s.id === subscriber.id ? { ...s, status: 'suspended' } : s
          ));
          toast.success('تم إيقاف الحساب');
        }
        break;
      case 'activate':
        try {
          await axios.post(`${API}/admin/subscribers/${subscriber.id}/activate`);
          toast.success('تم تفعيل الحساب');
          fetchData();
        } catch (e) {
          // Demo mode
          setSubscribers(subs => subs.map(s => 
            s.id === subscriber.id ? { ...s, status: 'active' } : s
          ));
          toast.success('تم تفعيل الحساب');
        }
        break;
      case 'delete':
        if (window.confirm('هل أنت متأكد من حذف هذا المشترك نهائياً؟')) {
          try {
            await axios.delete(`${API}/admin/subscribers/${subscriber.id}`);
            toast.success('تم حذف المشترك');
            fetchData();
          } catch (e) {
            setSubscribers(subs => subs.filter(s => s.id !== subscriber.id));
            toast.success('تم حذف المشترك');
          }
        }
        break;
      default:
        break;
    }
  };

  const filteredSubscribers = subscribers.filter(s => {
    const matchesSearch = !searchTerm || 
      s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    const matchesPlan = planFilter === 'all' || s.plan === planFilter;
    return matchesSearch && matchesStatus && matchesPlan;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-wide flex items-center gap-3">
            <Shield className="text-cyan-400" />
            {t('admin.title')}
          </h1>
          <p className="text-neutral-500 mt-1">{t('admin.subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="ghost" 
            onClick={() => setShowChangePassword(true)}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
          >
            <Key size={18} className={isArabic ? "ml-2" : "mr-2"} />
            {t('admin.changePassword')}
          </Button>
          <Button variant="ghost" onClick={fetchData}>
            <RefreshCw size={18} className={isArabic ? "ml-2" : "mr-2"} />
            {t('admin.refresh')}
          </Button>
          <Button 
            onClick={handleExportReport}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold shadow-lg shadow-emerald-500/30"
            data-testid="export-report-btn"
          >
            <Download size={18} className={isArabic ? "ml-2" : "mr-2"} />
            {t('admin.exportReport')}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label={t('admin.totalSubscribers')}
          value={stats.total_subscribers}
          color="#00F0FF"
        />
        <StatCard
          icon={CheckCircle}
          label={t('admin.activeSubscribers')}
          value={stats.active_subscribers}
          color="#00FF94"
          trend={`${Math.round(stats.active_subscribers / stats.total_subscribers * 100)}% ${isArabic ? 'نشط' : 'active'}`}
        />
        <StatCard
          icon={DollarSign}
          label={t('admin.totalRevenue')}
          value={`$${stats.total_revenue.toLocaleString()}`}
          color="#7000FF"
        />
        <StatCard
          icon={TrendingUp}
          label={t('admin.thisMonthRevenue')}
          value={`$${stats.this_month_revenue.toLocaleString()}`}
          color="#FF9500"
        />
      </div>

      {/* Tabs for different sections */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="glass mb-4">
          <TabsTrigger value="subscribers" className="flex items-center gap-2">
            <Users size={16} />
            {t('admin.members')}
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex items-center gap-2">
            <Receipt size={16} />
            {t('admin.payments')}
          </TabsTrigger>
          <TabsTrigger value="subscriptions" className="flex items-center gap-2">
            <CreditCard size={16} />
            {t('admin.subscriptions')}
          </TabsTrigger>
          <TabsTrigger value="wallet" className="flex items-center gap-2">
            <Wallet size={16} />
            {t('admin.wallet', 'Wallet')}
          </TabsTrigger>
          <TabsTrigger value="marketers" className="flex items-center gap-2">
            <Users size={16} />
            {t('admin.marketers', 'Marketers')}
          </TabsTrigger>
          <TabsTrigger value="withdrawals" className="flex items-center gap-2">
            <ArrowUpRight size={16} />
            {t('admin.withdrawals', 'Withdrawals')}
          </TabsTrigger>
          <TabsTrigger value="staff-access" className="flex items-center gap-2">
            <ShieldCheck size={16} />
            {t('admin.staffAccess')}
          </TabsTrigger>
        </TabsList>

        {/* Subscribers Tab */}
        <TabsContent value="subscribers">
          {/* Filters */}
          <div className="glass rounded-xl p-4 mb-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search className={`absolute ${isArabic ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-neutral-500`} size={18} />
                <Input
                  data-testid="search-subscribers"
                  placeholder={t('admin.searchSubscribers')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={isArabic ? "pr-10" : "pl-10"}
                />
              </div>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder={t('admin.status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('admin.allStatuses')}</SelectItem>
                  <SelectItem value="active">{t('admin.active')}</SelectItem>
                  <SelectItem value="suspended">{t('admin.suspended')}</SelectItem>
                  <SelectItem value="expired">{t('admin.expired')}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={planFilter} onValueChange={setPlanFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder={t('admin.plan')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('admin.allPlans')}</SelectItem>
                  {Object.values(PLANS).map(plan => (
                    <SelectItem key={plan.id} value={plan.id}>{getPlanName(plan)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Subscribers Table */}
          <div className="glass rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/10 flex flex-wrap items-center justify-between gap-4">
              <h3 className="font-semibold text-lg">{t('admin.members')} ({filteredSubscribers.length})</h3>
              <div className="flex gap-3">
                <Button 
                  onClick={handleCreateMemberFile}
                  className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-bold shadow-lg shadow-blue-500/30"
                  data-testid="create-member-file-btn"
                >
                  <Plus size={18} className={isArabic ? "ml-2" : "mr-2"} />
                  {t('admin.createFile')}
                </Button>
                <Button 
                  onClick={handleExportMembers}
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold shadow-lg shadow-emerald-500/30"
                  data-testid="export-members-btn"
                >
                  <Download size={18} className={isArabic ? "ml-2" : "mr-2"} />
                  {t('admin.exportFile')}
                </Button>
                <Button 
                  onClick={handleImportMembers}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold shadow-lg shadow-purple-500/30"
                  data-testid="import-members-btn"
                >
                  <Upload size={18} className={isArabic ? "ml-2" : "mr-2"} />
                  {t('admin.importFile')}
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t('admin.subscriber')}</th>
                    <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t('admin.plan')}</th>
                    <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t('admin.status')}</th>
                    <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t('admin.membersCollected')}</th>
                    <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t('admin.expiryDate')}</th>
                    <th className={`${isArabic ? 'text-right' : 'text-left'} px-4`}>{t('admin.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubscribers.map(subscriber => (
                    <SubscriberRow
                      key={subscriber.id}
                      subscriber={subscriber}
                      onAction={handleAction}
                      isArabic={isArabic}
                      t={t}
                      getPlanName={getPlanName}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <div className="glass rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <Receipt size={24} className="text-green-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{t('admin.paymentsRecord')}</h3>
                  <p className="text-sm text-neutral-500">{isArabic ? 'جميع عمليات الدفع على المنصة' : 'All platform payment transactions'}</p>
                </div>
              </div>
              <Button 
                onClick={() => {
                  const dataStr = JSON.stringify(payments, null, 2);
                  const dataBlob = new Blob([dataStr], { type: "application/json" });
                  const url = URL.createObjectURL(dataBlob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `payments-${new Date().toISOString().split('T')[0]}.json`;
                  link.click();
                  URL.revokeObjectURL(url);
                  toast.success(t('admin.exportPayments'));
                }}
                className="bg-gradient-to-r from-green-500 to-emerald-500"
              >
                <Download size={18} className={isArabic ? "ml-2" : "mr-2"} />
                {t('admin.exportPayments')}
              </Button>
            </div>
            <PaymentsTable payments={payments} loading={paymentsLoading} t={t} isArabic={isArabic} />
          </div>
        </TabsContent>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions">
          <div className="glass rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center">
                  <CreditCard size={24} className="text-violet-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{t('admin.activeSubscriptions')}</h3>
                  <p className="text-sm text-neutral-500">{isArabic ? 'جميع الاشتراكات على المنصة' : 'All platform subscriptions'}</p>
                </div>
              </div>
              <Button 
                onClick={() => {
                  const dataStr = JSON.stringify(subscriptions, null, 2);
                  const dataBlob = new Blob([dataStr], { type: "application/json" });
                  const url = URL.createObjectURL(dataBlob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `subscriptions-${new Date().toISOString().split('T')[0]}.json`;
                  link.click();
                  URL.revokeObjectURL(url);
                  toast.success(t('admin.exportSubscriptions'));
                }}
                className="bg-gradient-to-r from-violet-500 to-purple-500"
              >
                <Download size={18} className={isArabic ? "ml-2" : "mr-2"} />
                {t('admin.exportSubscriptions')}
              </Button>
            </div>
            <SubscriptionsTable subscriptions={subscriptions} loading={subscriptionsLoading} t={t} isArabic={isArabic} getPlanName={getPlanName} />
          </div>
        </TabsContent>

        {/* Staff Access Tab */}
        <TabsContent value="staff-access">
          <StaffAccessManager token={getToken()} t={t} isArabic={isArabic} />
        </TabsContent>

        {/* Wallet Tab */}
        <TabsContent value="wallet">
          <AdminWallet />
        </TabsContent>

        {/* Marketers Tab */}
        <TabsContent value="marketers">
          <AdminMarketers token={getToken()} />
        </TabsContent>

        {/* Withdrawals Tab */}
        <TabsContent value="withdrawals">
          <AdminWithdrawals token={getToken()} />
        </TabsContent>
      </Tabs>

      {/* Subscriber Detail Modal */}
      <SubscriberDetail
        subscriber={selectedSubscriber}
        onClose={() => setSelectedSubscriber(null)}
        onAction={handleAction}
        t={t}
        isArabic={isArabic}
        getPlanName={getPlanName}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        t={t}
        isArabic={isArabic}
      />
    </div>
  );
}
